import { type Dispatch } from 'react';

import { isAbortedError, toAppError } from '@/api/errors';
import { type ResponseMeta } from '@/api/types';
import { type PaymentMethodCode, type WalletAccountType } from '@/constants/deposit';
import { serverNow } from '@/helpers/serverTime';
import { getMethods, postChoose } from '@/modules/methods/api/methods.api';
import { clientIntentForSingleApiOption } from '@/modules/methods/singleApiOption';
import { postProof } from '@/modules/proof/api/proof.api';
import { getSession, getStatus } from '@/modules/session/api/session.api';
import { type PortalDeposit } from '@/schemas/cashierSession.schema';

import { type Action } from './types';

const inFlight = {
  resolve: false,
  refresh: false,
  methods: false,
  choose: false,
  proof: false,
};

/** Test seam. */
export function resetInFlightForTests(): void {
  inFlight.resolve = false;
  inFlight.refresh = false;
  inFlight.methods = false;
  inFlight.choose = false;
  inFlight.proof = false;
}

/**
 * GET locks must drop the instant the caller aborts. React Strict Mode (and any effect cleanup)
 * aborts then immediately starts a new call; if the flag stayed true until `finally`, the remount
 * would no-op and the page would sit on the skeleton forever.
 */
function acquireGetLock(slot: 'resolve' | 'refresh' | 'methods', signal: AbortSignal): boolean {
  if (inFlight[slot]) return false;
  inFlight[slot] = true;
  signal.addEventListener(
    'abort',
    () => {
      inFlight[slot] = false;
    },
    { once: true },
  );
  return true;
}

function depositFromSession(session: {
  id: string;
  publicId: string;
  merchantId: string;
  traderId: string | null;
  playerRef: string;
  status: string;
  mode: PortalDeposit['mode'];
  amount: string;
  receivedAmount: string | null;
  currency: string;
  decimalPlaces: number;
  merchantOrderId: string | null;
  paymentMethodCode: string | null;
  providerReference: string | null;
  createdAt: string;
  expiresAt: string | null;
  verificationExpiresAt: string | null;
  updatedAt: string;
  returnUrl: string | null;
}): PortalDeposit {
  return {
    id: session.id,
    publicId: session.publicId,
    merchantId: session.merchantId,
    traderId: session.traderId,
    playerRef: session.playerRef,
    status: session.status,
    mode: session.mode,
    amount: session.amount,
    receivedAmount: session.receivedAmount,
    currency: session.currency,
    decimalPlaces: session.decimalPlaces,
    merchantOrderId: session.merchantOrderId,
    paymentMethodCode: session.paymentMethodCode,
    providerReference: session.providerReference,
    createdAt: session.createdAt,
    expiresAt: session.expiresAt,
    verificationExpiresAt: session.verificationExpiresAt,
    updatedAt: session.updatedAt,
    returnUrl: session.returnUrl,
  };
}

export async function resolveSession(
  dispatch: Dispatch<Action>,
  token: string,
  signal: AbortSignal,
): Promise<void> {
  if (!acquireGetLock('resolve', signal)) return;
  dispatch({ type: 'RESOLVE_STARTED', token });
  try {
    let meta: ResponseMeta | undefined;
    const session = await getSession(token, signal, (responseMeta) => {
      meta = responseMeta;
    });
    dispatch({
      type: 'RESOLVE_SUCCEEDED',
      session,
      at: serverNow(),
      requestId: meta?.requestId,
    });
  } catch (error) {
    if (isAbortedError(error) || signal.aborted) return;
    dispatch({ type: 'RESOLVE_FAILED', error: toAppError(error) });
  } finally {
    inFlight.resolve = false;
  }
}

export async function refreshStatus(
  dispatch: Dispatch<Action>,
  token: string,
  signal: AbortSignal,
): Promise<void> {
  if (!acquireGetLock('refresh', signal)) return;
  try {
    let meta: ResponseMeta | undefined;
    const session = await getStatus(token, signal, (responseMeta) => {
      meta = responseMeta;
    });
    dispatch({
      type: 'SESSION_SYNCED',
      deposit: depositFromSession(session),
      walletInstructions: session.walletInstructions,
      at: serverNow(),
      requestId: meta?.requestId,
    });
  } catch (error) {
    if (isAbortedError(error) || signal.aborted) return;
    const appError = toAppError(error);
    dispatch({ type: 'REFRESH_FAILED', error: appError });
    // Re-throw so `usePolling` can stop on 401 / 429 / consecutive non-retryable failures.
    throw appError;
  } finally {
    inFlight.refresh = false;
  }
}

export async function fetchMethods(
  dispatch: Dispatch<Action>,
  token: string,
  signal: AbortSignal,
): Promise<void> {
  if (!acquireGetLock('methods', signal)) return;
  dispatch({ type: 'METHODS_LOAD_STARTED' });
  try {
    const { methods } = await getMethods(token, signal);
    const clientIntent = clientIntentForSingleApiOption(methods);
    dispatch({
      type: 'METHODS_LOAD_SUCCEEDED',
      tiles: methods,
      ...(clientIntent !== null ? { clientIntent } : {}),
    });
  } catch (error) {
    if (isAbortedError(error) || signal.aborted) return;
    dispatch({ type: 'METHODS_LOAD_FAILED', error: toAppError(error) });
  } finally {
    inFlight.methods = false;
  }
}

export async function chooseApiPath(
  dispatch: Dispatch<Action>,
  token: string,
  gatewayAccountId: string,
  payerWalletNumber: string,
  paymentMethodCode: PaymentMethodCode,
  signal: AbortSignal,
): Promise<void> {
  if (inFlight.choose) return;
  inFlight.choose = true;
  dispatch({ type: 'CHOOSE_STARTED' });
  try {
    const deposit = await postChoose(
      token,
      { path: 'api', gatewayAccountId, payerWalletNumber, paymentMethodCode },
      signal,
    );
    dispatch({ type: 'CHOOSE_SUCCEEDED', deposit, at: serverNow() });
  } catch (error) {
    if (isAbortedError(error) || signal.aborted) {
      dispatch({ type: 'CHOOSE_ABORTED' });
      return;
    }
    dispatch({ type: 'CHOOSE_FAILED', error: toAppError(error) });
  } finally {
    inFlight.choose = false;
  }
}

/**
 * One logical operation: POST /choose then GET /:token. If choose succeeds and refetch fails,
 * the choose result is kept and retry must re-fetch only (never re-POST).
 */
export async function chooseWalletPath(
  dispatch: Dispatch<Action>,
  token: string,
  accountType: WalletAccountType,
  traderWalletId: string,
  signal: AbortSignal,
): Promise<void> {
  if (inFlight.choose) return;
  inFlight.choose = true;
  dispatch({ type: 'CHOOSE_STARTED' });
  try {
    const deposit = await postChoose(
      token,
      { path: 'wallet', accountType, traderWalletId },
      signal,
    );
    try {
      let meta: ResponseMeta | undefined;
      const session = await getSession(token, signal, (responseMeta) => {
        meta = responseMeta;
      });
      dispatch({
        type: 'CHOOSE_WALLET_SUCCEEDED',
        deposit: depositFromSession(session),
        walletInstructions: session.walletInstructions,
        at: serverNow(),
        requestId: meta?.requestId,
      });
    } catch (refetchError) {
      if (isAbortedError(refetchError) || signal.aborted) {
        dispatch({ type: 'CHOOSE_ABORTED' });
        return;
      }
      dispatch({ type: 'CHOOSE_SUCCEEDED', deposit, at: serverNow() });
      dispatch({ type: 'REFRESH_FAILED', error: toAppError(refetchError) });
    }
  } catch (error) {
    if (isAbortedError(error) || signal.aborted) {
      dispatch({ type: 'CHOOSE_ABORTED' });
      return;
    }
    const appError = toAppError(error);
    dispatch({ type: 'CHOOSE_FAILED', error: appError });
    if (appError.status === 409) {
      const reconciled = await refetchSession(dispatch, token, signal);
      if (reconciled) dispatch({ type: 'CONFLICT_REFRESHED' });
      return;
    }
    if (appError.status === 422) {
      await fetchMethods(dispatch, token, signal);
    }
  } finally {
    inFlight.choose = false;
  }
}

export async function refetchSession(
  dispatch: Dispatch<Action>,
  token: string,
  signal: AbortSignal,
): Promise<boolean> {
  if (!acquireGetLock('refresh', signal)) return false;
  dispatch({ type: 'REFRESH_STARTED' });
  try {
    let meta: ResponseMeta | undefined;
    const session = await getSession(token, signal, (responseMeta) => {
      meta = responseMeta;
    });
    dispatch({
      type: 'SESSION_SYNCED',
      deposit: depositFromSession(session),
      walletInstructions: session.walletInstructions,
      at: serverNow(),
      requestId: meta?.requestId,
    });
    return true;
  } catch (error) {
    if (isAbortedError(error) || signal.aborted) return false;
    dispatch({ type: 'REFRESH_FAILED', error: toAppError(error) });
    return false;
  } finally {
    inFlight.refresh = false;
  }
}

export async function submitProof(
  dispatch: Dispatch<Action>,
  token: string,
  utr: string,
  file: File,
  onProgress: (percent: number | null) => void,
  signal: AbortSignal,
): Promise<void> {
  if (inFlight.proof) return;
  inFlight.proof = true;
  dispatch({ type: 'PROOF_SUBMIT_STARTED' });
  try {
    const deposit = await postProof(token, utr, file, onProgress, signal);
    dispatch({ type: 'PROOF_SUBMIT_SUCCEEDED', deposit, at: serverNow() });
  } catch (error) {
    if (isAbortedError(error) || signal.aborted) return;
    const appError = toAppError(error);
    dispatch({ type: 'PROOF_SUBMIT_FAILED', error: appError });
    // Duplicate-UTR CONFLICT keeps the player on the form with a field error. Only true sync
    // conflicts (no fieldErrors) refetch and show the "page refreshed" banner.
    if (appError.status === 409 && appError.fieldErrors === undefined) {
      const reconciled = await refetchSession(dispatch, token, signal);
      if (reconciled) dispatch({ type: 'CONFLICT_REFRESHED' });
    }
  } finally {
    inFlight.proof = false;
  }
}
